import React, { useState } from "react";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { toast } from "react-toastify";
import Avatar from "../inputs/Avatar";
import Switch from "../inputs/Switch";
import { getAccountStatus, secondsToHours } from "../../utils/helpers";
import moment from "moment";
import {
  useAdminInviteOtherAdminMutation,
  useAdminUpdateOtherAdminMutation,
} from "../../services/organization/settings";
import { useTranslation } from "react-i18next";

const AdminActionsMenu = ({
  element,
  accountStatus,
  updateAdmin,
  t,
  menuId,
}) => (
  <div className="dropdown tools">
    <button
      className="dropdown-toggle"
      type="button"
      id={menuId}
      data-bs-toggle="dropdown"
      aria-expanded="false"
      aria-label={t("common:actions")}
    >
      <span className="fas fa-ellipsis-h fa-fw font_14 font-color-grey"></span>
    </button>
    <ul className="dropdown-menu dropdown-menu-end" aria-labelledby={menuId}>
      <li>
        <div>
          <p className="bold mb-0 font_12 font-extrabold">{element?.name}</p>
          <p className="mb-0 font_12 font-color-grey">{element?.email}</p>
        </div>
      </li>
      <li>
        <hr className="dropdown-divider" />
      </li>
      <li>
        <Switch
          label={t("common:accountStatus")}
          disabled={accountStatus.type == "notResigtered"}
          checked={accountStatus.type == "active"}
          onChange={() =>
            updateAdmin({
              adminId: element?.id,
              updates: {
                active: !(accountStatus.type == "active"),
              },
            })
          }
        />
      </li>
    </ul>
  </div>
);

const AdminMobileCard = ({
  element,
  accountStatus,
  isPendingRegistration,
  isResending,
  onResend,
  updateAdmin,
  t,
}) => (
  <article className="admin-mobile-card">
    <div className="admin-mobile-card__header">
      <div className="admin-mobile-card__avatar">
        <Avatar width="56" height="56" avatarUrl={element?.avatar_url} />
      </div>
      <div className="admin-mobile-card__identity">
        <p className="admin-mobile-card__name mb-0">{element?.name}</p>
        <p className="admin-mobile-card__email mb-0">{element?.email}</p>
      </div>
      <div className="admin-mobile-card__menu">
        <AdminActionsMenu
          element={element}
          accountStatus={accountStatus}
          updateAdmin={updateAdmin}
          t={t}
          menuId={`admin-menu-mobile-${element?.id}`}
        />
      </div>
    </div>

    <ul className="admin-mobile-card__meta list-unstyled mb-0">
      <li>
        <span className="admin-mobile-card__label">
          {t("common:accountStatus")}
        </span>
        <span
          className="admin-mobile-card__value admin-mobile-card__status"
          style={{ color: accountStatus?.color }}
        >
          <span
            className="fas fa-circle fa-fw font_10 me-2"
            style={{ color: accountStatus?.color }}
            aria-hidden="true"
          ></span>
          {accountStatus?.name}
        </span>
      </li>
      <li>
        <span className="admin-mobile-card__label">
          {t("common:lastActivity")}
        </span>
        <span className="admin-mobile-card__value">
          {element?.last_activity
            ? moment(element.last_activity).fromNow()
            : "_"}
        </span>
      </li>
    </ul>

    {isPendingRegistration && (
      <div className="admin-mobile-card__footer">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm w-100"
          disabled={isResending}
          onClick={() => onResend(element.email)}
        >
          {isResending ? (
            <span className="fas fa-spinner fa-spin fa-fw me-1"></span>
          ) : null}
          {t("settings:resendInvitation")}
        </button>
      </div>
    )}
  </article>
);

const TableAdmin = ({ usersList }) => {
  const [updateAdmin] = useAdminUpdateOtherAdminMutation();
  const [inviteAdmin] = useAdminInviteOtherAdminMutation();
  const [resendingEmail, setResendingEmail] = useState(null);

  const { t } = useTranslation(["common", "settings"]);

  const handleResendInvitation = async (email) => {
    setResendingEmail(email);
    const result = await inviteAdmin(email);
    setResendingEmail(null);

    if (result?.error) {
      if (result.error.status === 429) {
        const seconds = result.error?.data?.data?.resend_available_in;
        const hours = secondsToHours(seconds);
        toast.error(
          t("settings:resendInvitationRateLimited", {
            message: result.error?.data?.message,
            hours,
          }),
        );
      } else {
        toast.error(result.error?.data?.message);
      }
      return;
    }

    toast.success(t("common:invitationSuccess"));
  };

  const admins = usersList?.data || [];

  return (
    <>
      <div className="admin-settings__desktop-table">
        <Table className="table table-admin">
          <Thead className="table-header">
            <Tr>
              <Th className="font_12 font-color-grey font-bold">
                {t("settings:adminName")}
              </Th>
              <Th className="font_12 font-color-grey font-bold">
                {t("common:inputs.email")}{" "}
              </Th>

              <Th className="font_12 font-color-grey font-bold">
                {t("common:accountStatus")}
              </Th>
              <Th className="font_12 font-color-grey font-bold">
                {t("settings:invitation")}
              </Th>
              <Th className="font_12 font-color-grey font-bold">
                {t("common:lastActivity")}
              </Th>
              <Th className="text-center font_12 font-color-grey font-bold">
                {t("common:actions")}
              </Th>
            </Tr>
          </Thead>
          <Tbody className="table__content">
            {admins.length
              ? admins.map((element, key) => {
                  const accountStatus = getAccountStatus(
                    element?.registered,
                    element?.active,
                    t,
                  );
                  const isPendingRegistration =
                    accountStatus.type === "notResigtered";
                  const isResending = resendingEmail === element?.email;

                  return (
                    <Tr key={element?.id || element?.email || key}>
                      <Td>
                        <div className="item me-2">
                          <Avatar
                            width="40"
                            height="40"
                            avatarUrl={element?.avatar_url}
                          />
                          <span className="bold font-extrabold font_14 me-2">
                            {element?.name}
                          </span>
                        </div>
                      </Td>
                      <Td>
                        <div className="item">
                          <p className="mb-0 font_14 font-color-grey">
                            {element?.email}
                          </p>
                        </div>
                      </Td>
                      <Td>
                        <div className="item d-flex align-center state-green">
                          <span
                            className="fas fa-circle fa-fw font_10 me-2"
                            style={{ color: accountStatus?.color }}
                          ></span>
                          <p
                            className="font_14 mb-0"
                            style={{ color: accountStatus?.color }}
                          >
                            {accountStatus?.name}
                          </p>
                        </div>
                      </Td>
                      <Td>
                        <div className="item">
                          {isPendingRegistration ? (
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              disabled={isResending}
                              onClick={() =>
                                handleResendInvitation(element.email)
                              }
                            >
                              {isResending ? (
                                <span className="fas fa-spinner fa-spin fa-fw me-1"></span>
                              ) : null}
                              {t("settings:resendInvitation")}
                            </button>
                          ) : (
                            <p className="mb-0 font_14 font-color-grey">_</p>
                          )}
                        </div>
                      </Td>
                      <Td>
                        <div className="item">
                          <p className="mb-0 font_14 ">
                            {element?.last_activity
                              ? moment(element?.last_activity).fromNow()
                              : "_"}
                          </p>
                        </div>
                      </Td>

                      <Td>
                        <div className="item text-center">
                          <AdminActionsMenu
                            element={element}
                            accountStatus={accountStatus}
                            updateAdmin={updateAdmin}
                            t={t}
                            menuId={`admin-menu-${element?.id}`}
                          />
                        </div>
                      </Td>
                    </Tr>
                  );
                })
              : null}
          </Tbody>
        </Table>
      </div>

      <div className="admin-settings__mobile-cards">
        {admins.length ? (
          <ul className="admin-mobile-cards list-unstyled mb-0">
            {admins.map((element) => {
              const accountStatus = getAccountStatus(
                element?.registered,
                element?.active,
                t,
              );
              const isPendingRegistration =
                accountStatus.type === "notResigtered";
              const isResending = resendingEmail === element?.email;

              return (
                <li key={element?.id || element?.email}>
                  <AdminMobileCard
                    element={element}
                    accountStatus={accountStatus}
                    isPendingRegistration={isPendingRegistration}
                    isResending={isResending}
                    onResend={handleResendInvitation}
                    updateAdmin={updateAdmin}
                    t={t}
                  />
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </>
  );
};

export default TableAdmin;
